import { PositionType } from 'data/types';
import { polygonBoundingBox } from 'utils/footprint';

type EdgeOrientation = 'h' | 'v' | 'd';

type EdgeLine = {
  point: PositionType;
  direction: PositionType;
};

const ORTHO_SNAP_ANGLE_DEG = 20;
const MAX_CLOSING_DUPLICATE_FEET = 2;
const CLOSING_DUPLICATE_RATIO = 0.2;

export function boundsMaxDimension(points: PositionType[]): number {
  const bounds = polygonBoundingBox(points);
  return Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY);
}

function perpendicularDistance(
  point: PositionType,
  lineStart: PositionType,
  lineEnd: PositionType
): number {
  const dx = lineEnd.x - lineStart.x;
  const dy = lineEnd.y - lineStart.y;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) {
    return Math.hypot(point.x - lineStart.x, point.y - lineStart.y);
  }
  const t =
    ((point.x - lineStart.x) * dx + (point.y - lineStart.y) * dy) / lengthSq;
  const projectedX = lineStart.x + t * dx;
  const projectedY = lineStart.y + t * dy;
  return Math.hypot(point.x - projectedX, point.y - projectedY);
}

export function simplifyPath(
  points: PositionType[],
  tolerance: number
): PositionType[] {
  if (points.length <= 2) return points;

  let maxDistance = 0;
  let maxIndex = 0;
  const lastIndex = points.length - 1;
  for (let index = 1; index < lastIndex; index += 1) {
    const distance = perpendicularDistance(
      points[index],
      points[0],
      points[lastIndex]
    );
    if (distance > maxDistance) {
      maxDistance = distance;
      maxIndex = index;
    }
  }

  if (maxDistance <= tolerance) {
    return [points[0], points[lastIndex]];
  }

  const left = simplifyPath(points.slice(0, maxIndex + 1), tolerance);
  const right = simplifyPath(points.slice(maxIndex), tolerance);
  return [...left.slice(0, -1), ...right];
}

function edgeOrientation(
  from: PositionType,
  to: PositionType
): EdgeOrientation {
  const angle =
    Math.abs(Math.atan2(to.y - from.y, to.x - from.x)) * (180 / Math.PI);
  const toHorizontal = Math.min(angle, 180 - angle);
  if (toHorizontal <= ORTHO_SNAP_ANGLE_DEG) return 'h';
  if (Math.abs(90 - angle) <= ORTHO_SNAP_ANGLE_DEG) return 'v';
  return 'd';
}

function dropClosingDuplicate(points: PositionType[]): PositionType[] {
  const first = points[0];
  const last = points[points.length - 1];
  const threshold = Math.min(
    MAX_CLOSING_DUPLICATE_FEET,
    boundsMaxDimension(points) * CLOSING_DUPLICATE_RATIO
  );
  return points.length > 3 &&
    Math.hypot(last.x - first.x, last.y - first.y) < threshold
    ? points.slice(0, -1)
    : points;
}

function mergeAlignedRuns(points: PositionType[]): PositionType[] {
  let merged = points;
  let changed = true;
  while (changed && merged.length > 3) {
    changed = false;
    for (let index = 0; index < merged.length; index += 1) {
      const previous = merged[(index - 1 + merged.length) % merged.length];
      const current = merged[index];
      const next = merged[(index + 1) % merged.length];
      const incoming = edgeOrientation(previous, current);
      const outgoing = edgeOrientation(current, next);
      if (incoming === outgoing && incoming !== 'd') {
        merged = merged.filter((_, pointIndex) => pointIndex !== index);
        changed = true;
        break;
      }
    }
  }
  return merged;
}

function edgeLine(from: PositionType, to: PositionType): EdgeLine {
  const midpoint = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  const orientation = edgeOrientation(from, to);
  if (orientation === 'h') {
    return { point: midpoint, direction: { x: 1, y: 0 } };
  }
  if (orientation === 'v') {
    return { point: midpoint, direction: { x: 0, y: 1 } };
  }
  const length = Math.hypot(to.x - from.x, to.y - from.y);
  return {
    point: midpoint,
    direction: { x: (to.x - from.x) / length, y: (to.y - from.y) / length }
  };
}

function intersectLines(a: EdgeLine, b: EdgeLine): PositionType | null {
  const cross = a.direction.x * b.direction.y - a.direction.y * b.direction.x;
  if (Math.abs(cross) < 0.02) return null;
  const dx = b.point.x - a.point.x;
  const dy = b.point.y - a.point.y;
  const t = (dx * b.direction.y - dy * b.direction.x) / cross;
  return { x: a.point.x + t * a.direction.x, y: a.point.y + t * a.direction.y };
}

export function orthogonalizeOutline(points: PositionType[]): PositionType[] {
  if (points.length < 3) return points;
  const merged = mergeAlignedRuns(dropClosingDuplicate(points));
  if (merged.length < 3) return points;
  const maxCornerShift = Math.min(8, boundsMaxDimension(merged) * 0.4);
  const lines = merged.map((point, index) =>
    edgeLine(point, merged[(index + 1) % merged.length])
  );
  return merged.map((vertex, index) => {
    const incoming = lines[(index - 1 + merged.length) % merged.length];
    const outgoing = lines[index];
    const intersection = intersectLines(incoming, outgoing);
    if (!intersection) return vertex;
    const shift = Math.hypot(
      intersection.x - vertex.x,
      intersection.y - vertex.y
    );
    return shift > maxCornerShift ? vertex : intersection;
  });
}

export function roundAndDedupe(points: PositionType[]): PositionType[] {
  const rounded: PositionType[] = [];
  for (const point of points) {
    const candidate = { x: Math.round(point.x), y: Math.round(point.y) };
    const previous = rounded[rounded.length - 1];
    if (!previous || previous.x !== candidate.x || previous.y !== candidate.y) {
      rounded.push(candidate);
    }
  }
  while (
    rounded.length > 1 &&
    rounded[0].x === rounded[rounded.length - 1].x &&
    rounded[0].y === rounded[rounded.length - 1].y
  ) {
    rounded.pop();
  }
  return rounded;
}
